
const DEFAULT_TTL = 36 * 1000 * 60 * 60; // 36 hours in milliseconds

export type PersistMode = 'local' | 'session';

const storageFor = (persist: PersistMode | undefined): Storage =>
    persist === 'session' ? sessionStorage : localStorage;

const otherStorageFor = (persist: PersistMode | undefined): Storage =>
    persist === 'session' ? localStorage : sessionStorage;

// ─── JWT decoding ────────────────────────────────────────────────────────────
// We do NOT verify the signature here (the server does that); we just peek at
// `exp` so we can size storage TTLs and short-circuit obviously-expired tokens
// before sending them on the wire.

export interface JwtPayload {
    exp?: number;     // seconds since epoch
    iat?: number;
    [k: string]: any;
}

export const decodeJwt = (token: string | null | undefined): JwtPayload | null => {
    if (!token || typeof token !== 'string') return null;
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    try {
        const padded = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        return JSON.parse(atob(padded));
    } catch {
        return null;
    }
};

/** Remaining lifetime of the JWT in ms (0 if missing / malformed / past `exp`). */
export const jwtRemainingMs = (token: string | null | undefined): number => {
    const decoded = decodeJwt(token);
    if (!decoded || typeof decoded.exp !== 'number') return 0;
    return Math.max(0, decoded.exp * 1000 - Date.now());
};

/**
 * Treat a JWT as expired this many ms early to absorb clock skew + the round
 * trip to the server. 30s is a comfortable margin for an access token with a
 * 30-minute lifetime.
 */
export const isJwtExpired = (token: string | null | undefined, skewMs = 30_000): boolean => {
    return jwtRemainingMs(token) <= skewMs;
};

// ─── Dual-storage (localStorage / sessionStorage) with expiry ────────────────
// Auth tokens want to live in localStorage when "Remember Me" is on (survives
// browser restarts) and in sessionStorage when it isn't (cleared when the tab
// is closed). Reads check both; writes go to one and clear the other so we
// never end up with a stale duplicate after the user toggles the checkbox.

interface StoredItem {
    value: any;
    expiry: number;
}

const readUnexpired = (storage: Storage, key: string): StoredItem | null => {
    const raw = storage.getItem(key);
    if (!raw) return null;
    try {
        const item = JSON.parse(raw) as StoredItem;
        if (!item || typeof item.expiry !== 'number' || Date.now() > item.expiry) {
            storage.removeItem(key);
            return null;
        }
        return item;
    } catch {
        storage.removeItem(key);
        return null;
    }
};

export const setItemWithExpiry = (key: string, value: any, ttl: number, persist: PersistMode = 'local'): void => {
    const item: StoredItem = { value, expiry: Date.now() + ttl };
    storageFor(persist).setItem(key, JSON.stringify(item));
    otherStorageFor(persist).removeItem(key);
};

export const getItemWithExpiry = (key: string): any => {
    return readUnexpired(localStorage, key)?.value
        ?? readUnexpired(sessionStorage, key)?.value
        ?? null;
};

/**
 * Where does the (unexpired) value for `key` currently live?
 * Used by the refresh handler to write the new token back to the same place
 * the previous one was — so a "Remember Me = false" session doesn't get
 * promoted to localStorage just because the access token rotated.
 */
export const getPersistModeForKey = (key: string): PersistMode | null => {
    if (readUnexpired(localStorage, key)) return 'local';
    if (readUnexpired(sessionStorage, key)) return 'session';
    return null;
};

// ─── Legacy localStorage-only API (used by non-auth callers) ─────────────────
// 33+ existing call sites store user prefs, last-visited IDs, etc. in
// localStorage with a manual TTL. They don't need the dual-storage behaviour,
// so we keep these wrappers unchanged.

export const setToLocalStorageWithExpiry = (key: string, value: any, ttl = DEFAULT_TTL) => {
    setItemWithExpiry(key, value, ttl, 'local');
};

export const getFromLocalStorageWithExpiry = (key: string) => {
    return getItemWithExpiry(key);
};

export const removeMultipleKeys = (keys: string[]) => {
    console.log('Storage Helper: Keys To Remove:', keys);
    keys.forEach(key => {
        localStorage.removeItem(key);
        sessionStorage.removeItem(key);
    });
};
